import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { useClient, useQuery } from 'jsonapi-react';
import { CourseMonitoringModule } from './course-monitoring-module';
import { CourseMonitoringModuleCard } from './course-monitoring-module-card';
import { CourseMonitoringLogActivity } from './course-monitoring-log-activity';
import TaskWidget from './TaskWidget';
import EventForm from '@/components/custom/schedule-modal';
import { CourseMonitoringLogImportant } from './course-monitoring-log-important';
import { t } from 'i18next';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { Card, CardContent, CardProgressWrapper } from '@/components/ui/card';
import { CourseBanner, CourseBannerContainer, CourseBannerContent, CourseBannerCTA, CourseBannerTitle } from '@/components/custom/course';
// import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaCursos from '@/assets/images/tia/TIA-CURSOS.png';
import explorarCursos from '@/assets/images/infantil/explorar-cursos.png';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader } from '@/components/ui/sheet';

export function CourseMonitoring(props) {
  const { uri } = props;

  const client = useClient();
  const courseId = parseInt(useParams().id);
  const { abilities } = usePlatformAbilities();
  const playfulStudent = usePlayfulStudent();
  const { data: course, isLoading: isLoadingCourse } = useQuery(`/courses/${courseId}?detailed=true`);

  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState();
  const [showFormEvent, setShowFormEvent] = useState(false);
  const [selectedTab, setSelectedTab] = useState('atualizacoes');
  const [moduleFiles, setModuleFiles] = useState([]);

  const moduleIds = course?.['learning-systems']?.map(module => module.id);

  // Arquivos dos módulos, exibidos na coluna lateral do tema lúdico.
  useEffect(() => {
    if (!playfulStudent || !moduleIds?.length) return;
    const query = moduleIds.map(id => `filter[learning_system_id][]=${id}`).join('&');
    client.fetch(`/learning_system_files?${query}`).then(response => {
      setModuleFiles(response?.data || []);
    });
  }, [moduleIds?.join()]);

  const parsedCourse = { ...course, 'learning-systems': course?.['learning-systems']?.sort((a, b) => a.order - b.order) };

  const fetchEvents = async initDate => {
    try {
      const initialDate = moment(initDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(initDate).endOf('month').format('YYYY-MM-DD');
      const courseFilter = courseId ? `&filter[course_id]=${courseId}` : '';
      const { data } = await client.fetch(`events?filter[date_between][initial]=${initialDate}&filter[date_between][final]=${endDate}${courseFilter}`);
      setEvents(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownloadCertificate = () => {
    window.open(`${process.env.REACT_APP_DEV_URL}//user_certificates/download/${parsedCourse?.certificate?.hash_id}.pdf`, '_blank');
  };

  useEffect(() => {
    fetchEvents();
  }, [courseId]);

  const closeFormEvent = () => {
    setShowFormEvent(false);
    setEditingEvent(null);
  };

  const courseProgress = Number(parsedCourse?.progress?.toFixed(2));

  const courseIsStarted = courseProgress > 0;

  const courseButtonLink = () => {
    // Fluxo infantil sem a tela "sobre" do módulo: o CTA leva direto pra sala
    // do próximo módulo incompleto (ou do primeiro, se nada foi iniciado).
    if (playfulStudent) {
      const target = parsedCourse?.['learning-systems']?.find(item => item.progress < 100) ?? parsedCourse?.['learning-systems']?.[0];
      return `/show-course/${target?.id}/classroom`;
    }

    if (courseProgress === 100) {
      const lastModule = parsedCourse?.['learning-systems']?.length - 1;
      return `/curso/${parsedCourse?.id}/modulo/${parsedCourse?.['learning-systems']?.[lastModule]?.id}/sobre`;
    } else if (courseProgress === 0) {
      const firstModule = parsedCourse?.['learning-systems']?.[0]?.id;
      return `/curso/${parsedCourse?.id}/modulo/${firstModule}/sobre`;
    } else {
      const nextModule = parsedCourse?.['learning-systems']?.find(item => item.progress < 100);
      return `/show-course/${nextModule?.id}/classroom`;
    }
  };

  const totalLessons = () => {
    const parsedItems = course?.['learning-systems']?.map(item => item?.learning_system_items_total_count);

    const totalLessons = parsedItems?.reduce((sum, curr) => {
      return sum + curr;
    }, 0);

    return totalLessons;
  };

  const hasModuleAndLesson = course?.['learning-systems']?.length > 0 && totalLessons() > 0;

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: '/curso',
        name: 'Cursos',
        isActive: false
      },
      {
        route: uri,
        name: parsedCourse?.title,
        'official-content': parsedCourse?.['official-content'] ?? false,
        isActive: true
      }
    ]
  };

  const modules = course?.['learning-systems'];

  const checkOpenModulesFinished = () => {
    const openModules = modules?.filter(module => {
      const moduleRequirements = module.learning_system_requirement_ids;
      const requiredModules = modules.filter(item => moduleRequirements?.includes(item.id));
      if (moduleRequirements.length === 0 || requiredModules?.every(item => item.finished === true)) {
        return module;
      }
    });
    const openModulesFinished = openModules?.every(item => item.progress === 100);

    return openModulesFinished;
  };

  if (isLoadingCourse) {
    return (
      <DashboardContent>
        <Loader />
      </DashboardContent>
    );
  }

  return (
    <DashboardContent
      breadcrumbs={breadcrumbs}
      variant="plain"
    >
      {playfulStudent ? (
        <>
          <TiaBanner
            className="mb-6 pb-6 md:mt-16"
            image={tiaCursos}
            imageHeight={180}
            imageOffset={150}
            bubbleMaxWidth={420}
            text={'Complete os módulos no seu ritmo — cada aula concluída te deixa mais perto do certificado! 🎓'}
          />

          <section
            className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
            style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 150ms both` }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
              style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
            />

            <div className="relative flex flex-wrap items-center gap-5">
              <img
                src={explorarCursos}
                alt=""
                aria-hidden="true"
                className="h-20 w-20 shrink-0 object-contain drop-shadow-sm"
              />
              <div className="min-w-[220px] flex-1">
                <h1
                  className="mb-1 text-2xl font-extrabold leading-snug"
                  style={{ color: '#222731' }}
                >
                  {parsedCourse?.title}
                </h1>

                {courseIsStarted && (
                  <div className="flex items-center gap-3">
                    <div className="h-2.5 w-full max-w-[240px] overflow-hidden rounded-full bg-white">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.min(courseProgress, 100)}%`, background: 'var(--gradient-progress-bar)' }}
                      />
                    </div>
                    <span className="whitespace-nowrap text-xs font-bold text-neutral-500">{courseProgress}% concluído</span>
                  </div>
                )}
              </div>

              {parsedCourse?.['finished-at'] && (
                <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-neutral-500 shadow-card">
                  🎓 Certificado a partir de {new Date(parsedCourse['finished-at']).toLocaleDateString('pt-br')}
                </span>
              )}
            </div>

            <div className="relative mt-6">
              {hasModuleAndLesson ? (
                checkOpenModulesFinished() ? (
                  <span className="block w-full cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 py-4 text-center text-sm font-bold text-neutral-400">Você concluiu os módulos disponíveis 🎉</span>
                ) : (
                  <Link
                    to={courseButtonLink()}
                    className="group/btn relative block w-full overflow-hidden rounded-full py-4 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
                    style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                      style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
                    />
                    <span className="relative">{courseIsStarted ? 'Continuar onde parei' : 'Começar'}</span>
                  </Link>
                )
              ) : (
                <p className="mb-0 text-sm font-semibold text-neutral-500">Este curso estará disponível em breve.</p>
              )}
            </div>

            <div className="relative mt-6 flex flex-col gap-3">
              {modules?.map((module, i) => {
                const moduleRequirements = module.learning_system_requirement_ids;
                const requiredModules = modules.filter(item => moduleRequirements?.includes(item.id));
                const canAccess = moduleRequirements.length === 0 || requiredModules?.every(item => item.progress > 90);
                const files = moduleFiles.filter(file => Number(file['learning-system-id']) === Number(module.id));

                return (
                  <CourseMonitoringModuleCard
                    key={module.id}
                    courseId={parsedCourse?.id}
                    newMessage
                    module={module}
                    canAccess={canAccess}
                    index={i}
                    files={files}
                  />
                );
              })}
            </div>
          </section>
        </>
      ) : (
        <Card className="mb-6">
          <CardContent className="w-full flex-grow-0">
            <CourseBanner
              backgroundUrl={parsedCourse?.image?.url}
              className="min-h-[224px]"
            >
              <CourseBannerContent>
                <CourseBannerContainer>
                  {/* <Badge
                  variant="primary-light"
                  size="xs"
                >
                  {parsedCourse?.knowledge}
                </Badge> */}

                  <CourseBannerTitle>{parsedCourse?.title}</CourseBannerTitle>

                  {courseIsStarted && (
                    <CardProgressWrapper
                      variant="cover"
                      toFixed={1}
                      percentage={courseProgress}
                    />
                  )}

                  <CourseBannerCTA>
                    {hasModuleAndLesson ? (
                      <Button
                        wide
                        disabled={checkOpenModulesFinished()}
                        asChild
                      >
                        <Link to={courseButtonLink()}>{courseIsStarted ? 'Continuar onde parei' : 'Começar'}</Link>
                      </Button>
                    ) : (
                      <p className="mb-0">Este curso estará disponível em breve.</p>
                    )}

                    {parsedCourse?.certificate?.hash_id && (
                      <Button
                        variant="outline"
                        wide
                        onClick={handleDownloadCertificate}
                        style={{ display: 'none' }}
                      >
                        {t('button.certification')}
                      </Button>
                    )}
                  </CourseBannerCTA>
                </CourseBannerContainer>
              </CourseBannerContent>
            </CourseBanner>
          </CardContent>
        </Card>
      )}

      {/* No tema lúdico os módulos já vivem no container acima; as abas
          (Atualizações/Sobre) seguem exclusivas do layout corporativo. */}
      {!playfulStudent && (
        <Tabs
          defaultValue={selectedTab}
          value={selectedTab}
          onValueChange={setSelectedTab}
        >
          {courseIsStarted && (
            <TabsList className="mb-6">
              <TabsTrigger value="atualizacoes">Atualizações</TabsTrigger>
              <TabsTrigger value="sobre">Sobre</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value={selectedTab}>
            {courseIsStarted && selectedTab === 'atualizacoes' && (
              <Page className="animate-fade-in">
                <PageContent>
                  <CourseMonitoringLogImportant />

                  <CourseMonitoringLogActivity
                    useType={1}
                    courseId={courseId}
                    title="Últimas atualizações do curso"
                  />
                </PageContent>

                <PageSidebar>
                  {abilities?.tarefas && <TaskWidget currentCourse={parsedCourse} />}

                  {/* <DashboardSidebar
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                onActiveStartDateChange={onActiveStartDateChange}
                eventDates={eventDates}
                setShowFormEvent={setShowFormEvent}
                events={events}
                /> */}
                </PageSidebar>
              </Page>
            )}

            {!courseIsStarted ? (
              <CourseMonitoringModule course={parsedCourse} />
            ) : (
              selectedTab === 'sobre' && (
                <div className="animate-fade-in">
                  <CourseMonitoringModule course={parsedCourse} />
                </div>
              )
            )}
          </TabsContent>
        </Tabs>
      )}

      <Sheet
        show={showFormEvent}
        onClose={closeFormEvent}
      >
        <SheetContent>
          <SheetHeader />
          <SheetBody>
            <EventForm
              onClose={() => setShowFormEvent(false)}
              editingEvent={editingEvent}
              events={events}
              setEvents={setEvents}
            />
          </SheetBody>
          <SheetFooter />
        </SheetContent>
      </Sheet>
    </DashboardContent>
  );
}
